import React, { ReactNode, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ActionButton } from './ActionButton';

// Module-level ref count for nested modal body lock management.
// Only the first modal to open locks the body; only the last to close unlocks it.
let openModalCount = 0;
let savedScrollY = 0;
let savedOverflow = '';

interface ModalProps {
  isOpen: boolean;
  handleClose: () => void;
  children: ReactNode | null;
  extraFooterButtons?: ReactNode | null;
  cancelText?: string;
  style?: React.CSSProperties;
}
export const Modal: React.FC<ModalProps> = ({
  isOpen,
  handleClose,
  children,
  extraFooterButtons,
  cancelText,
  style,
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const handleCloseRef = useRef(handleClose);
  handleCloseRef.current = handleClose;

  // Handle escape key press
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleCloseRef.current();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  // Prevent background scrolling (ref-counted for nested modals)
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    openModalCount++;
    if (openModalCount === 1) {
      // First modal — lock body and save state
      savedScrollY = window.scrollY;
      savedOverflow = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${savedScrollY}px`;
      document.body.style.left = '0';
      document.body.style.width = '100%';
    }

    return () => {
      openModalCount--;
      if (openModalCount === 0) {
        // Last modal — restore body
        document.body.style.overflow = savedOverflow;
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.width = '';
        window.scrollTo(0, savedScrollY);
      }
    };
  }, [isOpen]);

  // Handle wheel scroll events on modal content
  const handleWheelScroll = (event: React.WheelEvent) => {
    if (!contentRef.current) {
      return;
    }
    // Let the content div handle scrolling naturally
    // Only prevent default if we're at scroll boundaries to avoid page scroll
    const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
    const atTop = scrollTop === 0;
    const atBottom = scrollTop + clientHeight >= scrollHeight;

    if ((atTop && event.deltaY < 0) || (atBottom && event.deltaY > 0)) {
      event.preventDefault();
    }
  };

  if (!isOpen) {
    return null;
  }

  return createPortal(
    <div
      onClick={handleClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
        touchAction: 'none', // Prevent touch scrolling on mobile
        overscrollBehavior: 'contain', // Prevent scroll chaining
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        onWheel={handleWheelScroll}
        style={{
          backgroundColor: 'white',
          padding: '2rem',
          borderRadius: 15,
          maxWidth: '900px',
          width: '90%',
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 20px rgba(0, 0, 0, 0.1)',
          position: 'relative',
          ...style,
        }}
      >
        {/* Close Button */}
        <button
          onClick={handleClose}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            background: 'none',
            border: 'none',
            fontSize: '20px',
            color: '#666',
            cursor: 'pointer',
            padding: '8px',
            borderRadius: '4px',
            zIndex: 10,
          }}
        >
          ✕
        </button>

        <div
          ref={contentRef}
          style={{
            flex: 1,
            overflow: 'auto',
            marginBottom: '1rem',
            touchAction: 'auto',
          }}
        >
          {children}
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row-reverse',
            gap: 8,
            flexShrink: 0,
          }}
        >
          <ActionButton onClick={handleClose} variant="neutral">
            {cancelText || 'Close'}
          </ActionButton>
          {extraFooterButtons}
        </div>
      </div>
    </div>,
    document.body
  );
};
